(() => {
  const elementName = 'lnkbliss-infinite-recommendations';
  if (customElements.get(elementName)) return;
  customElements.define(elementName, class extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-rec-grid]');
      this.button = this.querySelector('[data-rec-more]');
      this.status = this.querySelector('[data-rec-status]');
      this.sentinel = this.querySelector('[data-rec-sentinel]');
      this.nextURL = this.dataset.feedUrl;
      this.seen = new Set([this.dataset.productId]);
      this.pages = new Set();
      this.controllers = new Set();
      this.ready = false;
      this.busy = false;
      this.failed = false;
      this.epoch = (this.epoch || 0) + 1;
      const epoch = this.epoch;
      this.grid.querySelectorAll('[data-rec-item]').forEach(item => this.seen.add(item.dataset.productId));
      this.onClick = () => { this.failed = false; this.loadMore(); };
      this.button.addEventListener('click', this.onClick);
      this.moreObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.failed) this.loadMore();
      }, { rootMargin: '0px 0px 400px 0px' });
      this.startObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.startObserver.disconnect();
        this.initialize(epoch);
      }, { rootMargin: '0px 0px 600px 0px' });
      this.startObserver.observe(this);
    }

    disconnectedCallback() {
      this.epoch++;
      this.startObserver?.disconnect();
      this.moreObserver?.disconnect();
      this.controllers?.forEach(controller => controller.abort());
      this.button?.removeEventListener('click', this.onClick);
    }

    eligible(item) {
      return item.dataset.productId && item.dataset.productId !== this.dataset.productId &&
        (this.dataset.squishyOnly !== 'true' || item.dataset.squishy === 'true');
    }

    async request(url) {
      const controller = new AbortController();
      this.controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const page = doc.querySelector('[data-rec-page]');
        if (!page) throw new Error('Recommendation content is missing');
        return page;
      } finally {
        clearTimeout(timer);
        this.controllers.delete(controller);
      }
    }

    async initialize(epoch) {
      try {
        const page = await this.request(this.dataset.relatedUrl);
        if (!this.isConnected || this.epoch !== epoch) return;
        // Keep focused cards stable if a customer has already started interacting.
        if (!this.grid.contains(document.activeElement)) {
          const candidates = [...page.querySelectorAll('[data-rec-item]'), ...this.grid.children];
          const unique = new Set([this.dataset.productId]);
          const cards = candidates.filter(item => {
            if (!this.eligible(item) || unique.has(item.dataset.productId)) return false;
            unique.add(item.dataset.productId);
            return true;
          }).slice(0, 12);
          if (cards.length) this.grid.replaceChildren(...cards);
        }
      } catch {
        // Server-rendered collection cards remain usable when related recommendations are unavailable.
      }
      if (!this.isConnected || this.epoch !== epoch) return;
      this.seen = new Set([this.dataset.productId, ...[...this.grid.children].map(item => item.dataset.productId)]);
      this.ready = true;
      this.button.hidden = false;
      this.moreObserver.observe(this.sentinel);
    }

    feedURL(value) {
      if (!value) return '';
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid recommendation page');
      url.searchParams.set('section_id', 'lnkbliss-recommendation-feed');
      url.searchParams.set('sort_by', 'best-selling');
      return url.href;
    }

    async loadMore() {
      if (!this.ready || this.busy || !this.nextURL) return;
      const epoch = this.epoch;
      this.busy = true;
      this.button.disabled = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading more recommendations…';
      this.moreObserver.unobserve(this.sentinel);
      try {
        let added = 0;
        // Skip pages containing only already-seen products without getting stuck on the sentinel.
        while (this.nextURL && !added) {
          const cursor = this.feedURL(this.nextURL);
          if (this.pages.has(cursor)) throw new Error('Repeated recommendation page');
          const page = await this.request(cursor);
          if (!this.isConnected || this.epoch !== epoch) return;
          const next = this.feedURL(page.dataset.next);
          if (next && (next === cursor || this.pages.has(next))) throw new Error('Repeated recommendation page');
          const fragment = document.createDocumentFragment();
          page.querySelectorAll('[data-rec-item]').forEach(item => {
            if (!this.eligible(item) || this.seen.has(item.dataset.productId)) return;
            this.seen.add(item.dataset.productId);
            fragment.append(item);
            added++;
          });
          this.grid.append(fragment);
          this.pages.add(cursor);
          this.nextURL = next;
        }
        this.failed = false;
        this.button.textContent = 'Load more';
        this.button.hidden = !this.nextURL;
        this.status.textContent = this.nextURL ? '' : 'You’ve seen all the recommendations.';
      } catch {
        if (!this.isConnected || this.epoch !== epoch) return;
        this.failed = true;
        this.button.textContent = 'Try again';
        this.button.hidden = false;
        this.status.textContent = 'Couldn’t load more products. Please try again.';
      } finally {
        if (this.isConnected && this.epoch === epoch) {
          this.busy = false;
          this.button.disabled = false;
          this.grid.setAttribute('aria-busy', 'false');
          if (this.nextURL && !this.failed) this.moreObserver.observe(this.sentinel);
        }
      }
    }
  });
})();
